import { ErrorMessage, Form, Formik } from "formik"
import { useMemo } from "react"
import { Col } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import * as Yup from "yup"

import Button from "@/components/Button/Button"
import TextInput from "@/components/Inputs/TextInput"
import { MODALS } from "@/constants/modals"
import { useEditSocialLinksMutation } from "@/queries/mutations/studentMutations"
import type { ProfileLinks } from "@/types/profileTypes"
import { useStore } from "@/zustand/store"

export default function SocialLinksModal() {
  const { t } = useTranslation()
  const editModalState = useStore((state) => state.editModalState)
  const editSocialLinks = useStore(
    (state) => state.modalsStates[MODALS.profile.editSocialLinks]
  )

  const editSocialLinksMutation = useEditSocialLinksMutation()

  const SocialLinksSchema = Yup.object().shape({
    github: Yup.string().url(t("socialLinksModal.errors.url")).nullable(),
    linkedin: Yup.string().url(t("socialLinksModal.errors.url")).nullable(),
    behance: Yup.string().url(t("socialLinksModal.errors.url")).nullable(),
    facebook: Yup.string().url(t("socialLinksModal.errors.url")).nullable(),
    youtube: Yup.string().url(t("socialLinksModal.errors.url")).nullable(),
    stackoverflow: Yup.string()
      .url(t("socialLinksModal.errors.url"))
      .nullable(),
    devto: Yup.string().url(t("socialLinksModal.errors.url")).nullable(),
  })

  const initialValues = useInitialSocialLinks(editSocialLinks)

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={SocialLinksSchema}
        enableReinitialize
        onSubmit={(values) => {
          editSocialLinksMutation.mutate(values, {
            onSuccess: () =>
              editModalState(MODALS.profile.editSocialLinks, undefined),
          })
        }}
      >
        {() => {
          return (
            <Form className="row">
              <Col md={6} className="mb-3">
                <TextInput
                  label={t("socialLinksModal.data.github.label")}
                  placeholder={t("socialLinksModal.data.github.placeholder")}
                  id="github"
                  name="github"
                  error={<ErrorMessage name="github" />}
                  as="field"
                />
              </Col>

              <Col md={6} className="mb-3">
                <TextInput
                  label={t("socialLinksModal.data.linkedin.label")}
                  placeholder={t("socialLinksModal.data.linkedin.placeholder")}
                  id="linkedin"
                  name="linkedin"
                  error={<ErrorMessage name="linkedin" />}
                  as="field"
                />
              </Col>

              <Col md={6} className="mb-3">
                <TextInput
                  label={t("socialLinksModal.data.behance.label")}
                  placeholder={t("socialLinksModal.data.behance.placeholder")}
                  id="behance"
                  name="behance"
                  error={<ErrorMessage name="behance" />}
                  as="field"
                />
              </Col>

              <Col md={6} className="mb-3">
                <TextInput
                  label={t("socialLinksModal.data.facebook.label")}
                  placeholder={t("socialLinksModal.data.facebook.placeholder")}
                  id="facebook"
                  name="facebook"
                  error={<ErrorMessage name="facebook" />}
                  as="field"
                />
              </Col>

              <Col md={6} className="mb-3">
                <TextInput
                  label={t("socialLinksModal.data.youtube.label")}
                  placeholder={t("socialLinksModal.data.youtube.placeholder")}
                  id="youtube"
                  name="youtube"
                  error={<ErrorMessage name="youtube" />}
                  as="field"
                />
              </Col>

              <Col md={6} className="mb-3">
                <TextInput
                  label={t("socialLinksModal.data.stackoverflow.label")}
                  placeholder={t(
                    "socialLinksModal.data.stackoverflow.placeholder"
                  )}
                  id="stackoverflow"
                  name="stackoverflow"
                  error={<ErrorMessage name="stackoverflow" />}
                  as="field"
                />
              </Col>

              <Col md={6} className="mb-3">
                <TextInput
                  label={t("socialLinksModal.data.devto.label")}
                  placeholder={t("socialLinksModal.data.devto.placeholder")}
                  id="devto"
                  name="devto"
                  error={<ErrorMessage name="devto" />}
                  as="field"
                />
              </Col>

              <div className={`d-flex justify-content-end mt-4`}>
                <div className="d-flex gap-3 align-self-start">
                  <Button
                    onClick={() =>
                      editModalState(MODALS.profile.editSocialLinks, undefined)
                    }
                    type="button"
                    variant={"normal"}
                  >
                    {t("buttons.cancel")}
                  </Button>

                  <Button
                    isLoading={editSocialLinksMutation.isPending}
                    type="submit"
                  >
                    {t("buttons.save")}
                  </Button>
                </div>
              </div>
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}

function useInitialSocialLinks(editSocialLinks?: ProfileLinks) {
  return useMemo<ProfileLinks>(() => {
    if (!editSocialLinks) return {} as ProfileLinks
    else
      return {
        github: editSocialLinks.github || "",
        linkedin: editSocialLinks.linkedin || "",
        behance: editSocialLinks.behance || "",
        facebook: editSocialLinks.facebook || "",
        youtube: editSocialLinks.youtube || "",
        stackoverflow: editSocialLinks.stackoverflow || "",
        devto: editSocialLinks.devto || "",
      } as ProfileLinks
  }, [editSocialLinks])
}
